import { AfterViewInit, ChangeDetectorRef, Component, Input, OnDestroy, OnInit, ViewChildren } from '@angular/core';
import { Page } from '../../../models/page';
import { PageService } from '../../../services/page.service';
import { ScrollPageComponent } from '../scroll-page/scroll-page.component';
import { ApplicationStateService } from '../../../services/application-state.service';
import { CommunicationService } from '../../../services/communication.service';
import { Subscription } from 'rxjs';
import { PositioningService } from '../../../services/positioning.service';
import { Position } from '../../../models/position';
import { ZoomService } from '../../../services/zoom.service';
import { ChapterService } from '../../../services/chapter.service';
import { DeviceDetectorService } from 'ngx-device-detector';
import $ from 'jquery';
import { SidebarService } from '../../../services/sidebar.service';
import { sidebarConfig } from '../../../../environments/configs/sidebar-config';
import { NgStyle } from '@angular/common';

@Component({
    selector: 'app-scroll-pages',
    templateUrl: './scroll-pages.component.html',
    styleUrls: ['./scroll-pages.component.scss'],
    standalone: true,
    imports: [
        ScrollPageComponent,
        NgStyle
    ]
})
export class ScrollPagesComponent implements OnInit, AfterViewInit, OnDestroy {

    static LOAD_DURING_SCROLL = 5;

    @Input() imageWidth: number;
    @Input() imageHeight: number;

    @Input() container: JQuery;
    @ViewChildren(ScrollPageComponent) scrollPageComponents: Array<ScrollPageComponent>;

    pages: Array<Page>;
    currentPages: Array<Page>;

    pageTurnAnnounceSubscription: Subscription;
    pageTurnConfirmedSubscription: Subscription;
    zoomSubscription: Subscription;
    scrollContainer;
    scrollContainerMinHeight;
    totalContainerHeight = 0;
    marginLeft: number;
    childLeftOffset: number;
    updateIndex = true;
    // This is needed to prevent page jumping when opening the sidebar or resizing the window.
    stopIndexAndFrameCalculation = false;
    // When this variable is set, the calculation of the Index and the Page Frame is prevented.
    lowerDummyPages;
    upperDummyPages;
    dummyPageHeight: number;
    dummyPageMargin = 20;
    amountLowerDummyPages: number;
    amountUpperDummyPages: number;
    isChrome: boolean;
    isFirefox: boolean;
    isIe: boolean;
    isWindows: boolean;
    isIOS: boolean;
    isSafari: boolean;
    private sidebarSubscription: Subscription;

    constructor(public pageService: PageService,
                private chapterService: ChapterService,
                public applicationStateService: ApplicationStateService,
                private zoomService: ZoomService,
                private deviceService: DeviceDetectorService,
                private positioningService: PositioningService,
                private sidebarService: SidebarService,
                private changeDetectionRef: ChangeDetectorRef,
                private communicationService: CommunicationService) {

        this.pageTurnAnnounceSubscription = this.communicationService.pageTurnAnnounced$.subscribe(ele => {
            this.loadCurrentPages(false);
            if (!(this.applicationStateService.currentIndex === 0 && ele.moveToPage > 0)) {
                this.pageService.scrollToActivePage();
            }
        });

        this.pageTurnConfirmedSubscription = this.communicationService.pageTurnConfirmed$.subscribe(ele => {
            this.loadCurrentPages(false);
            this.pageService.updateURLParameter();
        });

        this.zoomSubscription = this.communicationService.zoomLevelChangedConfirmed$.subscribe(ele => {

            this.updateIndex = false;
            this.getPageHeight();
            this.calculateTotalContainerHeight();

            if (!this.applicationStateService.isZoomed) {
                return;
            }

            setTimeout(() => {
                this.updateIndex = true;
            }, 50);
        });

        this.sidebarSubscription = this.sidebarService.sidebarEventFired$.subscribe(event => {
            setTimeout(() => {
                this.updatePageSizes();
            }, sidebarConfig.sidebarTransitionSpeed);
        });

    }

    ngOnInit(): void {
        this.scrollContainerMinHeight = window.window.innerHeight * ScrollPagesComponent.LOAD_DURING_SCROLL;
        this.pages = this.pageService.getAllPages();
        this.loadCurrentPages();
        this.changeDetectionRef.detectChanges();
        this.isChrome = this.deviceService.getDeviceInfo().browser === 'chrome';
        this.isFirefox = this.deviceService.getDeviceInfo().browser === 'firefox';
        this.isSafari = this.deviceService.getDeviceInfo().browser === 'safari';
        this.isIe = this.deviceService.getDeviceInfo().browser === 'ie' ||
            this.deviceService.getDeviceInfo().browser === 'ms-edge';
        this.isWindows = this.deviceService.getDeviceInfo().os === 'windows';
        this.isIOS = this.deviceService.getDeviceInfo().browser === 'safari' &&
            (this.deviceService.getDeviceInfo().device === 'iphone' || this.deviceService.getDeviceInfo().device === 'ipad');
    }

    ngOnDestroy(): void {
        this.pageTurnAnnounceSubscription.unsubscribe();
        this.pageTurnConfirmedSubscription.unsubscribe();
        this.zoomSubscription.unsubscribe();
        this.sidebarSubscription.unsubscribe();
    }

    ngAfterViewInit(): void {
        this.getScrollContainer();

        if (this.applicationStateService.currentIndex !== 0) {
            this.pageService.scrollToActivePage();
        }
        this.getPageHeight();
        this.onScroll();
    }

    getContainer(): JQuery {
        return this.container;
    }

    onScroll(): void {

        if (!this.updateIndex) {
            setTimeout(() => {
                this.updateIndex = true;
            }, 100);
        }

        this.loadCurrentPages(!this.pageService.isScrolling);

        this.scrollPageComponents.forEach(scrollPageComponent => {
            scrollPageComponent.handlePageTurn();
        });

    }

    getScrollPageComponents(): Array<ScrollPageComponent> {
        return this.scrollPageComponents;
    }

    getActiveAndNeighbourPages(): Array<ScrollPageComponent> {

        const activePages = [];
        this.scrollPageComponents.forEach(page => {
            if (page.active || page.isNeighbour) {
                activePages.push(page);
            }
        });

        return activePages;
    }

    dummyIdentifier(index, item): number {
        return index;
    }

    calculateTotalContainerHeight(): void {
        this.totalContainerHeight = (this.dummyPageHeight + this.dummyPageMargin) * this.pageService.getAllPages().length;
    }

    updatePageSizes(): void {
        this.getPageHeight();
        this.loadCurrentPages(false);
    }

    private loadCurrentPages(calculateIndex?: boolean): void {

        // If the method should determine the new Index
        if (calculateIndex && !this.pageService.isScrolling) {
            this.calculateIndex();
        }

        this.calculatePageFrame();
    }

    private getPageHeight(): number {

        const pagePosition: Position = this.positioningService.fitPage(
            this.pages[0].image.width,
            this.pages[0].image.height
        );
        this.dummyPageHeight = pagePosition.h * this.zoomService.getCurrentZoomLevel();

        return this.dummyPageHeight;
    }

    private calculateIndex(): void {

        if (this.stopIndexAndFrameCalculation) {
            return;
        }

        const pageHeight = this.getPageHeight();
        const scrollTop = this.getScrollContainer().scrollTop();
        const calculatedIndex = Math.max(Math.ceil((scrollTop - (pageHeight + 20 / 2)) / (pageHeight + 20)), 0);

        if (this.applicationStateService.currentIndex !== calculatedIndex) {

            // In case you scroll from last page to second last page the index want update correctly and the app sets
            // the index again to the last page. Which results in not scrolling up and stucking on last page
            // To fix this issue, the index won't get updated when moving from last page to second last page
            if (this.applicationStateService.currentIndex >= this.pages.length - 1 &&
                calculatedIndex >= this.pages.length - 2) {

                return;
            }

            this.applicationStateService.currentIndex = calculatedIndex;

            this.communicationService.confirmPageTurn(this.pageService.getCurrentPages());
            this.chapterService.getChapterFromIndex(this.applicationStateService.currentIndex).then(chapter => {
                this.chapterService.turnToChapter(chapter);
            });
        }
    }

    private calculatePageFrame(): void {

        if (this.stopIndexAndFrameCalculation) {
            return;
        }

        // Don't move frame all the time
        if (
            this.currentPages && this.currentPages.length > 0 &&
            this.applicationStateService.currentIndex > this.currentPages[1].index &&
            this.applicationStateService.currentIndex < this.currentPages[this.currentPages.length - 2].index) {
            return;
        }

        const minIndex: number = Math.max(
            this.applicationStateService.currentIndex - ScrollPagesComponent.LOAD_DURING_SCROLL,
            0
        );
        const maxIndex: number = Math.min(
            this.applicationStateService.currentIndex + ScrollPagesComponent.LOAD_DURING_SCROLL,
            this.pages.length
        );

        const newPages = this.pages.slice(minIndex, maxIndex);
        this.currentPages = newPages;
        this.calculateTotalContainerHeight();
        this.calculateDummyPages(newPages);

    }

    private calculateDummyPages(currentPages): void {

        this.amountLowerDummyPages = Math.max(this.pageService.hasLimitedView() ?
                this.pageService.getGetViewablePagePossition(currentPages[0]) :
                currentPages[0].index,
            0);
        this.amountUpperDummyPages = this.pages.length - currentPages.length - this.amountLowerDummyPages;

        this.lowerDummyPages = new Array(this.amountLowerDummyPages);
        this.upperDummyPages = new Array(this.amountUpperDummyPages);
    }

    private getScrollContainer(): JQuery {
        if (!this.scrollContainer) {
            this.scrollContainer = $('#scrollablePagesContainer');
        }

        return this.scrollContainer;
    }

}
